import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { updateHomelessInfoField } from 'redux/actions/homeless';
import { Homeless } from 'types';
import { syncDebitBalance as syncGiveCardBalance } from 'api/givecard';
import { syncDebitBalance as syncPexBalance } from 'api/pex';

interface SyncBalanceState {
    isLoading: boolean;
    isComplete: boolean;
    error: string | null;
}

const useMemberSyncDebitBalance = (homeless: Homeless) => {
    const dispatch = useDispatch();

    const [state, setState] = useState<SyncBalanceState>({
        isLoading: false,
        isComplete: false,
        error: null
    });

    const reset = useCallback(() => {
        setState({
            isLoading: false,
            isComplete: false,
            error: null
        });
    }, []);

    const execute = useCallback(async () => {
        if (state.isComplete) {
            reset();
            return;
        }

        setState(prev => ({
            ...prev,
            isLoading: true,
            error: null
        }));

        try {
            const cardType = homeless.card?.card_type?.toLowerCase();
            if (!cardType) {
                throw new Error('No card type specified');
            }

            let response;
            try {
                switch (cardType) {
                    case 'pex':
                        response = await syncPexBalance(homeless.id);
                        break;
                    case 'givecard':
                    case 'virtual-givecard':
                        response = await syncGiveCardBalance(homeless.id);
                        break;
                    default:
                        throw new Error('Invalid card type');
                }
            } catch (apiError) {
                throw new Error(apiError instanceof Error ? apiError.message : 'Sync API call failed');
            }

            if (!response.ok) {
                const errorMessage = await response.text();
                throw new Error(errorMessage || 'Failed to sync debit balance');
            }

            setState({
                isLoading: false,
                isComplete: true,
                error: null
            });

            dispatch(updateHomelessInfoField('last_sync', new Date().toISOString()));
            setTimeout(reset, 3000);

        } catch (error) {
            setState({
                isLoading: false,
                isComplete: false,
                error: error instanceof Error ? error.message : 'An unknown error occurred'
            });
        }
    }, [homeless.id, homeless.card?.card_type, dispatch, state.isComplete, reset]);

    return {
        execute,
        ...state
    };
};

export default useMemberSyncDebitBalance;
